import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import FaqSection from '../../components/Faq';
import CodeBlock from '../../components/CodeBlock';
import { useTheme } from '../../theme';
import { articleSchema, faqPageSchema, breadcrumbSchema, Faq } from '../../lib/schema';

const PATH = '/jwt-base64url-encoding';

const FAQS: Faq[] = [
  { q: 'Why do JWTs use Base64URL instead of standard Base64?', a: 'JWTs are meant to sit inside URLs, headers and cookies. Standard Base64\'s "+" and "/" characters have special meaning in URLs, so JWTs use the URL-safe alphabet ("-" and "_") and drop padding to avoid any escaping.' },
  { q: 'What are the three parts of a JWT?', a: 'A JWT is header.payload.signature — the header and payload are JSON objects Base64URL-encoded as text, and the signature is a cryptographic signature (also Base64URL-encoded) over the first two parts.' },
  { q: 'Can I read a JWT payload without a secret key?', a: 'Yes — the header and payload are just Base64URL-encoded JSON, not encrypted, so anyone can decode and read them. Only the signature requires the secret/private key, and that\'s what proves the token was not tampered with.' },
  { q: 'Is a decodable JWT payload a security problem?', a: 'Only if you put sensitive data in it. JWTs provide integrity (tamper-evidence via the signature), not confidentiality — never put passwords or secrets in a JWT payload.' },
];

export default function JwtBase64UrlPage() {
  const { themeClasses } = useTheme();
  const heading = `text-2xl font-bold ${themeClasses.textPrimary} mt-10 mb-4`;
  const para = `${themeClasses.textSecondary} leading-relaxed mb-4`;

  return (
    <>
      <Seo
        title="How JWT Tokens Use Base64URL Encoding | EasyBase64"
        description="Learn how JSON Web Tokens (JWT) use Base64URL encoding for the header, payload and signature, why they're readable but not writable, and how to decode one yourself."
        path={PATH}
        jsonLd={[
          articleSchema({
            headline: 'How JWT Tokens Use Base64URL Encoding',
            description: 'How JSON Web Tokens structure their header, payload and signature using Base64URL encoding.',
            path: PATH,
            datePublished: '2026-07-07',
            dateModified: '2026-07-07',
          }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'JWT Base64URL Encoding', path: PATH }]),
        ]}
      />

      <div className="max-w-3xl mx-auto">
        <article className={`${themeClasses.infoCard} rounded-2xl p-6 sm:p-8 md:p-12 border mb-12`}>
          <h1 className={`text-4xl font-bold tracking-tight ${themeClasses.textPrimary} mb-6`}>
            How JWT Tokens Use Base64URL Encoding
          </h1>

          <p className={para}>
            Every JSON Web Token (JWT) you've seen — in an Authorization header, a cookie, or an
            OAuth response — is built from three Base64URL-encoded segments. Understanding that
            encoding demystifies what a JWT actually is: not a black box, but readable JSON wrapped
            for safe transport.
          </p>

          <h2 className={heading}>The three segments</h2>
          <p className={para}>
            A JWT has the shape <code>header.payload.signature</code>, three Base64URL strings
            joined by dots. The header and payload each start life as a plain JSON object — for
            example <code>{'{"alg":"HS256","typ":"JWT"}'}</code> — that gets Base64URL-encoded into
            text. The signature is a cryptographic hash of the first two segments, also
            Base64URL-encoded, computed with a secret or private key.
          </p>

          <CodeBlock
            language="Example JWT"
            code={`eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiIxMjM0NTY3ODkwIn0.dozjgNryP4J3jVmNHl0w5N_XgL0n3I9PlFUP0THsR8U`}
          />

          <h2 className={heading}>Why Base64URL and not standard Base64?</h2>
          <p className={para}>
            JWTs are designed to travel inside URLs, HTTP headers and cookies without any extra
            escaping. Standard Base64's <code>+</code> and <code>/</code> characters (and{' '}
            <code>=</code> padding) have special meaning or awkward encoding requirements in those
            contexts, so the JWT spec (RFC 7519) mandates{' '}
            <Link to="/url-safe-base64" className="underline">Base64URL</Link>: <code>-</code> and{' '}
            <code>_</code> instead of <code>+</code> and <code>/</code>, with padding omitted
            entirely.
          </p>

          <h2 className={heading}>Readable, but not writable</h2>
          <p className={para}>
            Because Base64URL is just an encoding (see{' '}
            <Link to="/is-base64-encryption" className="underline">is Base64 encryption?</Link>),
            anyone can decode a JWT's header and payload and read the JSON inside — paste the first
            two segments into the <Link to="/base64-decode" className="underline">Base64 decoder</Link>{' '}
            (with <code>-</code>/<code>_</code> swapped back to <code>+</code>/<code>/</code>) to see
            for yourself. What an attacker <em>can't</em> do is forge a new signature without the
            secret or private key, which is what makes a JWT tamper-evident even though its contents
            are fully visible.
          </p>

          <h2 className={heading}>The practical takeaway</h2>
          <p className={para}>
            Never put secrets, passwords, or anything sensitive directly in a JWT payload — treat it
            as public information. Use the signature purely to verify the token hasn't been altered
            or forged, and pair JWTs with TLS in transit for real confidentiality.
          </p>
        </article>

        <FaqSection title="JWT Base64URL Encoding — FAQ" faqs={FAQS} />
      </div>
    </>
  );
}
